import React, { useCallback, useEffect, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import Breadcrumbs from "../../../Common/Breadcrumbs";
import ShimmerLoader from "../../../Common/ShimmerLoader";
import { PermissionWrapper } from "../../../components/PermissionWrapper/PermissionWrapper";
import { StatCard } from "../../../components/Ads/StatCard/StatCard";
import { useReportAnalytics, useAdvertisers, usePlacements } from "../hooks";

import "./Reports.css";

const formatINR = (value: number) =>
  "₹" + value.toLocaleString("en-IN");

const formatNum = (value: number) =>
  value.toLocaleString("en-IN");

const ReportsPage: React.FC = () => {
  const navigate = useNavigate();

  const [placement, setPlacement] = useState("ALL");
  const [advertiserId, setAdvertiserId] = useState("");
  const [dateFrom, setDateFrom] = useState("2024-06-01");
  const [dateTo, setDateTo] = useState("2026-06-30");

  const { values: advertiserValues } = useAdvertisers({ pageSize: 100, status: "ALL" });
  const { values: placementValues } = usePlacements();

  const { functions, values, loading, exporting } = useReportAnalytics({
    placement: "ALL",
    advertiserId: "",
    dateFrom: "2024-06-01",
    dateTo: "2026-06-30",
  });

  useEffect(() => {
    functions.setParams({ placement, advertiserId, dateFrom, dateTo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement, advertiserId, dateFrom, dateTo]);

  const handleExportReport = useCallback(async () => {
    try {
      const blob = await functions.exportReport();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ads-campaign-stats-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      // error surfaced via the error banner
    }
  }, [functions]);

  const analytics = values.analytics;

  const statCardsRow1 = [
    {
      title: "Total Impressions",
      value: analytics ? formatNum(analytics.totalImpressions) : "—",
      iconClassName: "ph-eye",
    },
    {
      title: "Total Clicks",
      value: analytics ? formatNum(analytics.totalClicks) : "—",
      iconClassName: "ph-hand-pointing",
      iconBgColor: "#FFF1F2",
      iconColor: "#F43F5E",
    },
    {
      title: "Average CTR",
      value: analytics ? `${analytics.avgCtr.toFixed(2)}%` : "—",
      iconClassName: "ph-target",
    },
  ];

  const statCardsRow2 = [
    {
      title: "Total Amount Spent",
      value: analytics ? formatINR(analytics.totalAmountSpent) : "—",
      iconClassName: "ph-currency-inr",
      valueTone: "danger" as const,
    },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <div className="mb-3" style={{ padding: "0 15px" }}>
          <Breadcrumbs
            items={[
              { label: "Ads", onClick: () => navigate("/ads/overview") },
              { label: "Reports", active: true },
            ]}
          />
        </div>

        {/* Header row */}
        <div className="ads-reports__header-row">
          <div className="ads-reports__title-block">
            <h1 className="ads-reports__title">Reports</h1>
            <p className="ads-reports__subtitle">Campaign performance analytics</p>
          </div>
          <PermissionWrapper anyOfPermissions={["ads.admin", "ads.manage", "ads.view"]} hideWhenDisabled>
            <button
              type="button"
              className="ads-reports__export-btn"
              onClick={handleExportReport}
              disabled={exporting}
            >
              <i className={`${exporting ? "ph-spinner" : "ph-download-simple"} ads-reports__export-btn-icon`} aria-hidden="true" />
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
          </PermissionWrapper>
        </div>

        {/* Filter bar */}
        <div className="ads-reports__filters">
          <div className="ads-reports__filters-left">
            <div className="ads-reports__filter-group">
              <label className="ads-reports__filter-label">Placement</label>
              <div className="ads-reports__select-wrapper">
                <select
                  className="ads-reports__select"
                  value={placement}
                  onChange={(e) => setPlacement(e.target.value)}
                  aria-label="Filter by placement"
                >
                  <option value="ALL">All</option>
                  {placementValues.placements.map((p) => (
                    <option key={p.id} value={p.name}>
                      {p.name.split("_").map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(" ")}
                    </option>
                  ))}
                </select>
                <i className="ph-caret-down ads-reports__select-caret" aria-hidden="true" />
              </div>
            </div>

            <div className="ads-reports__filter-group">
              <label className="ads-reports__filter-label">Advertiser</label>
              <div className="ads-reports__select-wrapper">
                <select
                  className="ads-reports__select"
                  value={advertiserId}
                  onChange={(e) => setAdvertiserId(e.target.value)}
                  aria-label="Filter by advertiser"
                >
                  <option value="">All</option>
                  {advertiserValues.advertisers.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
                <i className="ph-caret-down ads-reports__select-caret" aria-hidden="true" />
              </div>
            </div>
          </div>

          <div className="ads-reports__filters-right">
            <div className="ads-reports__filter-group">
              <label className="ads-reports__filter-label">Date (From)</label>
              <div className="ads-reports__date-wrapper">
                <input
                  type="date"
                  className="ads-reports__date-input"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  aria-label="Date from"
                />
                <i className="ph-calendar-blank ads-reports__date-icon" aria-hidden="true" />
              </div>
            </div>

            <div className="ads-reports__filter-group">
              <label className="ads-reports__filter-label">Date (To)</label>
              <div className="ads-reports__date-wrapper">
                <input
                  type="date"
                  className="ads-reports__date-input"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  aria-label="Date to"
                />
                <i className="ph-calendar-blank ads-reports__date-icon" aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>

        {/* Stats row 1 */}
        {loading ? (
          <ShimmerLoader type="table" rows={4} />
        ) : (
          <>
            <Row className="g-3 mt-2">
              {statCardsRow1.map((card) => (
                <Col key={card.title} xs={12} sm={6} lg={4}>
                  <StatCard
                    title={card.title}
                    value={card.value}
                    iconClassName={card.iconClassName}
                    iconBgColor={card.iconBgColor}
                    iconColor={card.iconColor}
                  />
                </Col>
              ))}
            </Row>

            <Row className="g-3 mt-0">
              {statCardsRow2.map((card) => (
                <Col key={card.title} xs={12} sm={6} lg={4}>
                  <StatCard
                    title={card.title}
                    value={card.value}
                    iconClassName={card.iconClassName}
                    valueTone={card.valueTone}
                  />
                </Col>
              ))}
            </Row>
          </>
        )}

        {/* Campaign Breakdown */}
        {!loading && analytics && analytics.campaignBreakdown.length > 0 && (
          <div className="ads-reports__geo-card">
            <div className="ads-reports__geo-title">Campaign Breakdown</div>
            <div className="ads-table-wrapper">
              <table className="table ads-reports__geo-table">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Impressions</th>
                    <th>Clicks</th>
                    <th>CTR</th>
                    <th>Spend</th>
                    <th>Conversions</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.campaignBreakdown.map((row) => (
                    <tr key={row.campaignId}>
                      <td>{row.campaignName}</td>
                      <td>{formatNum(row.impressions)}</td>
                      <td>{formatNum(row.clicks)}</td>
                      <td>{row.ctr.toFixed(2)}%</td>
                      <td>{formatINR(row.spend)}</td>
                      <td>{formatNum(row.conversions)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {values.error && (
          <div style={{ padding: "0 15px", marginTop: 16 }}>
            <Alert variant="danger" dismissible onClose={() => functions.fetchAnalytics()}>
              {values.error}
            </Alert>
          </div>
        )}
      </Container>
    </div>
  );
};

export default ReportsPage;
